'use client';
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Grid, Card, CardContent,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, Snackbar, Avatar, CircularProgress, Backdrop,
  Tabs, Tab, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, LinearProgress, Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Search, Save, School, Grade, Assignment, MenuBook, 
  Visibility, Print, Download, Refresh, 
  Assessment, CloudUpload, FileDownload, CheckCircle, Add
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import api from '../services/api';

const schoolColors = {
  primary:   '#1a237e',
  secondary: '#0d47a1',
  accent:    '#ff6f00',
  gold:      '#ffd700',
  white:     '#ffffff',
};

const GRADE_SCALE = [
  { min: 75, grade: 'A' },
  { min: 65, grade: 'B' },
  { min: 50, grade: 'C' },
  { min: 30, grade: 'D' },
  { min: 0,  grade: 'F' },
];

function getGrade(percentage) {
  const p = parseFloat(percentage);
  if (isNaN(p)) return 'N/A';
  for (const s of GRADE_SCALE) if (p >= s.min) return s.grade;
  return 'F';
}

function gradeColor(grade) {
  return {
    A: { bgcolor: '#e8f5e920', color: '#2e7d32', border: '#2e7d3240' },
    B: { bgcolor: '#e3f2fd20', color: '#1565c0', border: '#1565c040' },
    C: { bgcolor: '#fff8e120', color: '#f57f17', border: '#f57f1740' },
    D: { bgcolor: '#fff3e020', color: '#e65100', border: '#e6510040' },
    F: { bgcolor: '#ffebee20', color: '#c62828', border: '#c6282840' },
  }[grade] || { bgcolor: '#f5f5f5', color: '#616161', border: '#61616140' };
}

const SchoolButton = styled(Button)(() => ({
  borderRadius: 8,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  color: schoolColors.white,
  '&:hover': {
    background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': { background: '#ccc', color: '#888' },
}));

const StyledCard = styled(Card)(() => ({
  background: schoolColors.white,
  borderRadius: 16,
  border: `1px solid ${schoolColors.primary}15`,
  boxShadow: `0 4px 20px ${schoolColors.primary}10`,
}));

export default function TeacherMarksEntry() {
  const [activeTab, setActiveTab] = useState(0); // 0: Single Student, 1: Excel Bulk Upload, 2: View Results, 3: Class Performance Report
  const [terms, setTerms] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  
  // Single Student Mode
  const [searchReg, setSearchReg] = useState('');
  const [student, setStudent] = useState(null);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Bulk Excel Upload Mode
  const [bulkLevel, setBulkLevel] = useState('form1');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkTerm, setBulkTerm] = useState('');
  const [bulkExam, setBulkExam] = useState('');
  const [excelData, setExcelData] = useState([]);
  const [excelUploading, setExcelUploading] = useState(false);

  // Password Modal
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');

  // View Results Mode
  const [viewSearchReg, setViewSearchReg] = useState('');
  const [viewStudent, setViewStudent] = useState(null);
  const [viewResults, setViewResults] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedViewTerm, setSelectedViewTerm] = useState('');
  const [studentTerms, setStudentTerms] = useState([]);

  // Class Report Mode
  const [reportLevel, setReportLevel] = useState('form1');
  const [reportTerm, setReportTerm] = useState('');
  const [reportExam, setReportExam] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const toast = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    (async () => {
      try {
        const [tRes, eRes, subRes] = await Promise.all([
          api.get('/terms'),
          api.get('/exam-types'),
          api.get('/subjects'),
        ]);
        const termsArr = Array.isArray(tRes.data) ? tRes.data : [];
        setTerms(termsArr);
        setExamTypes(Array.isArray(eRes.data) ? eRes.data : []);
        setSubjects(Array.isArray(subRes.data) ? subRes.data : []);
        const current = termsArr.find(t => t.is_current);
        if (current) {
          setSelectedTerm(current.id);
          setBulkTerm(current.id);
          setReportTerm(current.id);
        }
      } catch (e) {
        toast('Failed to load initial data', 'error');
      } finally {
        setInitialLoad(false);
      }
    })();
  }, []);

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      setPasswordError('Please fill all password fields');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast('Your password has been changed successfully!');
      setPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordError('');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    }
  };

  // Search single student
  const handleSearch = async () => {
    if (!searchReg.trim()) return;
    setLoading(true);
    try {
      const { data: s } = await api.get(
        `/students/search?registration=${encodeURIComponent(searchReg.trim())}`
      );
      setStudent(s);
      const { data: subs } = await api.get(`/students/${s.id}/subjects`);
      const arr = Array.isArray(subs) ? subs : [];
      setSubjects(arr);
      
      const init = {};
      arr.forEach(sub => {
        init[sub.id] = { marks_obtained: '', total_marks: 100, remarks: '', percentage: null };
      });
      setMarks(init);
    } catch (e) {
      toast(e.response?.data?.error || 'Student not found', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkChange = (subId, field, value) => {
    setMarks(prev => {
      const updated = { ...prev, [subId]: { ...prev[subId], [field]: value } };
      const obtained = parseFloat(updated[subId].marks_obtained);
      const total    = parseFloat(updated[subId].total_marks) || 100;
      updated[subId].percentage =
        !isNaN(obtained) && total > 0 ? parseFloat(((obtained / total) * 100).toFixed(2)) : null;
      return updated;
    });
  };

  const handleSaveSingle = async () => {
    if (!selectedTerm || !selectedExam) {
      toast('Select a term and exam type', 'warning');
      return;
    }
    const entered = Object.entries(marks).filter(([, d]) => d.marks_obtained !== '');
    if (entered.length === 0) {
      toast('Enter marks for at least one subject', 'warning');
      return;
    }
    setSubmitting(true);
    let saved = 0;
    for (const [subId, d] of entered) {
      try {
        await api.post('/marks', {
          student_id:    student.id,
          subject_id:    parseInt(subId),
          term_id:       parseInt(selectedTerm),
          exam_type_id:  parseInt(selectedExam),
          marks_obtained: parseFloat(d.marks_obtained),
          total_marks:    parseFloat(d.total_marks) || 100,
          remarks:        d.remarks || '',
        });
        saved++;
      } catch (e) {
        console.error(e);
      }
    }
    setSubmitting(false);
    toast(`${saved} marks saved successfully for ${student.name}`);
  };

  // Download Excel Template for Class
  const handleDownloadExcelTemplate = async () => {
    try {
      const res = await api.get('/admin/students');
      const allStudents = Array.isArray(res.data) ? res.data : [];
      const classFiltered = allStudents.filter(s => s.applying_for_level === bulkLevel);
      
      const templateData = classFiltered.length > 0
        ? classFiltered.map(s => ({
            'Registration Number': s.registration_number,
            'Student Name': `${s.first_name} ${s.middle_name || ''} ${s.last_name}`.trim(),
            'Marks Obtained (0-100)': '',
            'Total Marks': 100,
            'Remarks': ''
          }))
        : [
            {
              'Registration Number': 'S.12345/NECTA2026',
              'Student Name': 'Mfano Mwanafunzi',
              'Marks Obtained (0-100)': '75',
              'Total Marks': 100,
              'Remarks': 'Vizuri'
            }
          ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Marks_Template');
      XLSX.writeFile(wb, `Kaozya_${bulkLevel}_Marks_Template.xlsx`);
      toast(`Downloaded Excel template for ${bulkLevel.toUpperCase()}`);
    } catch (e) {
      toast('Failed to generate template', 'error');
    }
  };

  // Parse Uploaded Excel File
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const data = XLSX.utils.sheet_to_json(ws);

        if (data.length === 0) {
          toast('Excel file is empty', 'error');
          return;
        }

        const formatted = data.map(row => ({
          registration_number: row['Registration Number'] || row['registration_number'] || row['REG NO'] || row['Reg Number'] || '',
          student_name: row['Student Name'] || row['student_name'] || row['NAME'] || '',
          marks_obtained: row['Marks Obtained (0-100)'] || row['Marks'] || row['marks_obtained'] || row['Score'] || 0,
          total_marks: row['Total Marks'] || row['total_marks'] || 100,
          remarks: row['Remarks'] || row['remarks'] || '',
        })).filter(r => r.registration_number);

        setExcelData(formatted);
        toast(`Loaded ${formatted.length} student marks from Excel`);
      } catch (err) {
        toast('Failed to parse Excel file', 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  // Submit Bulk Excel Marks
  const handleSaveBulkExcel = async () => {
    if (!bulkSubject || !bulkTerm || !bulkExam) {
      toast('Tafadhali chagua Somo, Muhula (Term) na Aina ya Mtihani kwanza', 'warning');
      return;
    }
    if (excelData.length === 0) {
      toast('Hakuna taarifa za wanafunzi zilizopakiwa kwenye Excel', 'warning');
      return;
    }

    setExcelUploading(true);
    try {
      const payload = {
        marks: excelData.map(d => ({
          registration_number: d.registration_number,
          subject_id: parseInt(bulkSubject),
          term_id: parseInt(bulkTerm),
          exam_type_id: parseInt(bulkExam),
          marks_obtained: parseFloat(d.marks_obtained) || 0,
          total_marks: parseFloat(d.total_marks) || 100,
          remarks: d.remarks || '',
        }))
      };

      const res = await api.post('/marks/bulk', payload);
      toast(res.data?.message || 'Matokeo ya Excel yamehifadhiwa kikamilifu!', 'success');
      setExcelData([]);
    } catch (err) {
      toast(err.response?.data?.error || 'Imeshindikana kupandisha matokeo ya Excel', 'error');
    } finally {
      setExcelUploading(false);
    }
  };

  // Fetch Class Performance Report
  const handleLoadClassReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get('/admin/reports/marks', {
        params: {
          form_level: reportLevel,
          term_id: reportTerm,
          exam_type_id: reportExam || undefined,
        }
      });
      setReportData(res.data);
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to load report', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  // View Individual Student Results
  const handleViewSearch = async () => {
    if (!viewSearchReg.trim()) return;
    setViewLoading(true);
    try {
      const { data: s } = await api.get(`/students/search?registration=${encodeURIComponent(viewSearchReg.trim())}`);
      setViewStudent(s);
      const { data: marksData } = await api.get(`/students/${s.id}/marks`);
      setViewResults(marksData);
      
      const termsSet = new Set();
      if (marksData?.marks && Array.isArray(marksData.marks)) {
        marksData.marks.forEach(m => {
          if (m.term_name) termsSet.add(m.term_name);
        });
      }
      const arr = Array.from(termsSet);
      setStudentTerms(arr);
      if (arr.length > 0) setSelectedViewTerm(arr[0]);
    } catch (e) {
      toast(e.response?.data?.error || 'Student not found', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Top Banner */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#0d233a', color: 'white', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#ff6f00', fontWeight: 900 }}>K</Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>KAOZYA TEACHER & ACADEMIC PORTAL</Typography>
            <Typography variant="caption" sx={{ color: '#ffd700' }}>Logged in as: {user.full_name || user.username} ({user.role || 'Teacher'})</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setPasswordModal(true)} sx={{ color: '#ffd700', borderColor: '#ffd700', fontWeight: 700, textTransform: 'none', borderRadius: 2 }}>
            Change Password
          </Button>
          <Button variant="contained" size="small" color="error" onClick={() => { localStorage.clear(); window.location.href = '/admin/login'; }} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2 }}>
            Sign Out
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="1. Enter Marks (Mmoja Mmoja)" icon={<Grade />} iconPosition="start" sx={{ fontWeight: 700 }} />
        <Tab label="2. Excel Bulk Upload (Darasa Zima)" icon={<CloudUpload />} iconPosition="start" sx={{ fontWeight: 700 }} />
        <Tab label="3. View Student Results" icon={<Visibility />} iconPosition="start" sx={{ fontWeight: 700 }} />
        <Tab label="4. Class Performance & Rankings" icon={<Assessment />} iconPosition="start" sx={{ fontWeight: 700 }} />
      </Tabs>

      {/* ── TAB 0: Single Student Marks Entry ── */}
      {activeTab === 0 && (
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 3 }}>
              Kuingiza Matokeo kwa Mwanafunzi Mmoja (By Registration Number)
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Registration Number" placeholder="e.g. S.76827/NECTA2026" value={searchReg} onChange={e => setSearchReg(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
              </Grid>
              <Grid item xs={12} md={4}>
                <SchoolButton fullWidth size="large" onClick={handleSearch} disabled={loading || !searchReg.trim()} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}>
                  {loading ? 'Searching...' : 'Search Student'}
                </SchoolButton>
              </Grid>
            </Grid>

            {student && (
              <Box sx={{ mt: 4 }}>
                <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary }}>{student.name}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{student.registration_number}</Typography>
                      <Chip label={(student.form_level || '').replace('form', 'Form ')} size="small" sx={{ mt: 0.5, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Muhula (Term)</InputLabel>
                        <Select value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)} label="Muhula (Term)">
                          {terms.map(t => <MenuItem key={t.id} value={t.id}>{t.academic_year_name} — {t.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Aina ya Mtihani (Exam Type)</InputLabel>
                        <Select value={selectedExam} onChange={e => setSelectedExam(e.target.value)} label="Aina ya Mtihani (Exam Type)">
                          {examTypes.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: schoolColors.primary }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Subject Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} width={160}>Marks Obtained</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} width={140}>Total Possible</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} width={120}>Grade</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subjects.map(sub => {
                        const m = marks[sub.id] || { marks_obtained: '', total_marks: 100, remarks: '', percentage: null };
                        const g = getGrade(m.percentage);
                        const gc = gradeColor(g);
                        return (
                          <TableRow key={sub.id} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{sub.name}</TableCell>
                            <TableCell><Chip label={sub.code} size="small" /></TableCell>
                            <TableCell>
                              <TextField size="small" type="number" value={m.marks_obtained} onChange={e => handleMarkChange(sub.id, 'marks_obtained', e.target.value)} inputProps={{ min: 0, max: m.total_marks }} />
                            </TableCell>
                            <TableCell>
                              <TextField size="small" type="number" value={m.total_marks} onChange={e => handleMarkChange(sub.id, 'total_marks', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              {m.percentage !== null && <Chip label={g} size="small" sx={{ bgcolor: gc.bgcolor, color: gc.color, fontWeight: 800 }} />}
                            </TableCell>
                            <TableCell>
                              <TextField size="small" fullWidth placeholder="e.g. Excellent" value={m.remarks} onChange={e => handleMarkChange(sub.id, 'remarks', e.target.value)} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <SchoolButton size="large" onClick={handleSaveSingle} disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}>
                    {submitting ? 'Saving Marks...' : 'Save Student Marks'}
                  </SchoolButton>
                </Box>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}

      {/* ── TAB 1: Excel Bulk Marks Upload ── */}
      {activeTab === 1 && (
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 1 }}>
              Kupandisha Matokeo ya Darasa Zima kwa Excel (.xlsx / .csv)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Pakua template ya darasa lako, jaza alama za wanafunzi, kisha pakia faili la Excel moja kwa moja.
            </Typography>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Darasa / Kidato</InputLabel>
                  <Select value={bulkLevel} onChange={e => setBulkLevel(e.target.value)} label="Darasa / Kidato">
                    <MenuItem value="form1">Form 1</MenuItem>
                    <MenuItem value="form2">Form 2</MenuItem>
                    <MenuItem value="form3">Form 3</MenuItem>
                    <MenuItem value="form4">Form 4</MenuItem>
                    <MenuItem value="form5">Form 5</MenuItem>
                    <MenuItem value="form6">Form 6</MenuItem>
                    <MenuItem value="pre_form1">Pre-Form 1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Somo (Subject)</InputLabel>
                  <Select value={bulkSubject} onChange={e => setBulkSubject(e.target.value)} label="Somo (Subject)">
                    {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Muhula (Term)</InputLabel>
                  <Select value={bulkTerm} onChange={e => setBulkTerm(e.target.value)} label="Muhula (Term)">
                    {terms.map(t => <MenuItem key={t.id} value={t.id}>{t.academic_year_name} — {t.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Aina ya Mtihani (Exam Type)</InputLabel>
                  <Select value={bulkExam} onChange={e => setBulkExam(e.target.value)} label="Aina ya Mtihani (Exam Type)">
                    {examTypes.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Step 1 & 2 Action Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
              <Button variant="outlined" startIcon={<FileDownload />} onClick={handleDownloadExcelTemplate} sx={{ fontWeight: 700, borderRadius: 2, p: 1.5, textTransform: 'none' }}>
                1. Download Excel Template ({bulkLevel.toUpperCase()})
              </Button>
              <Button variant="contained" component="label" startIcon={<CloudUpload />} sx={{ bgcolor: schoolColors.secondary, fontWeight: 700, borderRadius: 2, p: 1.5, textTransform: 'none' }}>
                2. Select &amp; Upload Excel File (.xlsx)
                <input type="file" accept=".xlsx, .xls, .csv" hidden onChange={handleFileUpload} />
              </Button>
            </Stack>

            {/* Preview of Excel data */}
            {excelData.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: schoolColors.primary }}>
                    Wanafunzi {excelData.length} Wametambuliwa Kwenye Excel (Preview):
                  </Typography>
                  <SchoolButton onClick={handleSaveBulkExcel} disabled={excelUploading} startIcon={excelUploading ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}>
                    {excelUploading ? 'Inapakia...' : `Hifadhi Alama za Wanafunzi Wote (${excelData.length})`}
                  </SchoolButton>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 400, borderRadius: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead sx={{ bgcolor: schoolColors.primary }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Reg Number</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Student Name</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="center">Marks Obtained</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="center">Total Marks</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="center">Grade</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excelData.map((row, idx) => {
                        const pct = ((parseFloat(row.marks_obtained) || 0) / (parseFloat(row.total_marks) || 100)) * 100;
                        const g = getGrade(pct);
                        const gc = gradeColor(g);
                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{row.registration_number}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{row.student_name}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, color: schoolColors.primary }}>{row.marks_obtained}</TableCell>
                            <TableCell align="center">{row.total_marks}</TableCell>
                            <TableCell align="center">
                              <Chip label={g} size="small" sx={{ bgcolor: gc.bgcolor, color: gc.color, fontWeight: 800 }} />
                            </TableCell>
                            <TableCell>{row.remarks || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}

      {/* ── TAB 2: View Student Results ── */}
      {activeTab === 2 && (
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 3 }}>
              Angalia Matokeo ya Mwanafunzi (Student Report Card)
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Registration Number" placeholder="e.g. S.76827/NECTA2026" value={viewSearchReg} onChange={e => setSearchReg(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleViewSearch()} />
              </Grid>
              <Grid item xs={12} md={4}>
                <SchoolButton fullWidth size="large" onClick={handleViewSearch} disabled={viewLoading || !viewSearchReg.trim()} startIcon={viewLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}>
                  {viewLoading ? 'Searching...' : 'Search Results'}
                </SchoolButton>
              </Grid>
            </Grid>

            {viewStudent && viewResults && (
              <Box sx={{ mt: 4 }}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: schoolColors.primary }}>{viewStudent.name}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{viewStudent.registration_number}</Typography>
                    <Chip label={(viewStudent.form_level || '').replace('form', 'Form ')} size="small" sx={{ mt: 0.5, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
                  </Box>
                  <Button variant="contained" startIcon={<Print />} onClick={() => window.print()} sx={{ bgcolor: schoolColors.primary, borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>
                    Print Report Card (PDF)
                  </Button>
                </Paper>

                {viewResults.summary && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 800 }}>{viewResults.summary.average_percentage}%</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Wastani (Average)</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <Typography variant="h4" sx={{ color: '#1565c0', fontWeight: 800 }}>{viewResults.summary.overall_grade}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Daraja (Overall Grade)</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }}>
                        <Typography variant="h4" sx={{ color: '#f57f17', fontWeight: 800 }}>{viewResults.summary.total_marks}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Jumla ya Alama</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e8ff' }}>
                        <Typography variant="h4" sx={{ color: '#6b21a8', fontWeight: 800 }}>{viewResults.marks?.length || 0}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Masomo Yote</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: schoolColors.primary }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Subject</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Marks Obtained</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Total Marks</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Percentage</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Grade</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Exam Type</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 700 }}>Term</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewResults.marks || []).map((r, idx) => {
                        const gc = gradeColor(r.grade);
                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{r.subject_name}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, color: schoolColors.primary }}>{r.marks_obtained}</TableCell>
                            <TableCell align="center">{r.total_marks}</TableCell>
                            <TableCell align="center">{r.percentage}%</TableCell>
                            <TableCell align="center">
                              <Chip label={r.grade} size="small" sx={{ bgcolor: gc.bgcolor, color: gc.color, fontWeight: 800 }} />
                            </TableCell>
                            <TableCell>{r.exam_type_name}</TableCell>
                            <TableCell>{r.term_name}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}

      {/* ── TAB 3: Class Performance & Rankings ── */}
      {activeTab === 3 && (
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 1 }}>
              Ripoti ya Darasa Zima, Waliofeli / Waliofaulu &amp; Rankings
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Angalia takwimu za ufaulu wa darasa, msimamo wa nafasi (rankings), na mgawanyo wa madaraja.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kidato / Darasa</InputLabel>
                  <Select value={reportLevel} onChange={e => setReportLevel(e.target.value)} label="Kidato / Darasa">
                    <MenuItem value="form1">Form 1</MenuItem>
                    <MenuItem value="form2">Form 2</MenuItem>
                    <MenuItem value="form3">Form 3</MenuItem>
                    <MenuItem value="form4">Form 4</MenuItem>
                    <MenuItem value="form5">Form 5</MenuItem>
                    <MenuItem value="form6">Form 6</MenuItem>
                    <MenuItem value="pre_form1">Pre-Form 1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Muhula (Term)</InputLabel>
                  <Select value={reportTerm} onChange={e => setReportTerm(e.target.value)} label="Muhula (Term)">
                    {terms.map(t => <MenuItem key={t.id} value={t.id}>{t.academic_year_name} — {t.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aina ya Mtihani</InputLabel>
                  <Select value={reportExam} onChange={e => setReportExam(e.target.value)} label="Aina ya Mtihani">
                    <MenuItem value="">Mitihani Yote</MenuItem>
                    {examTypes.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <SchoolButton fullWidth size="large" onClick={handleLoadClassReport} disabled={reportLoading} startIcon={reportLoading ? <CircularProgress size={18} color="inherit" /> : <Assessment />}>
                  {reportLoading ? 'Inapakia...' : 'Toa Ripoti ya Darasa'}
                </SchoolButton>
              </Grid>
            </Grid>

            {reportData && (
              <Box sx={{ mt: 3 }}>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={2.4}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e0f2fe' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0369a1' }}>{reportData.summary?.total_students || 0}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Wanafunzi Wote</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#dcfce7' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#166534' }}>{reportData.summary?.total_passed || 0}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Waliofaulu (Pass)</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fee2e2' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#991b1b' }}>{reportData.summary?.total_failed || 0}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Waliofeli (Fail)</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fef3c7' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#92400e' }}>{reportData.summary?.pass_rate || 0}%</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Asilimia ya Ufaulu</Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e8ff' }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#6b21a8' }}>
                        A:{reportData.summary?.grade_distribution?.A || 0} B:{reportData.summary?.grade_distribution?.B || 0} C:{reportData.summary?.grade_distribution?.C || 0} D:{reportData.summary?.grade_distribution?.D || 0} F:{reportData.summary?.grade_distribution?.F || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Mgawanyo wa Madaraja</Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Rankings Table */}
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: schoolColors.primary }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }} align="center">Rank #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }}>Registration #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }}>Student Full Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }}>Gender</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }} align="center">Average %</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }} align="center">Grade</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800 }} align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(reportData.students || []).map((s, idx) => {
                        const gc = gradeColor(s.overall_grade);
                        return (
                          <TableRow key={idx} hover>
                            <TableCell align="center">
                              <Chip label={`#${s.rank}`} size="small" sx={{ fontWeight: 900, bgcolor: s.rank <= 3 ? '#ffd700' : '#f1f5f9', color: s.rank <= 3 ? '#000' : '#333' }} />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{s.registration_number}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{s.student_name}</TableCell>
                            <TableCell>{s.gender === 'ME' ? 'Male' : 'Female'}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, color: schoolColors.primary }}>{s.average_percentage}%</TableCell>
                            <TableCell align="center">
                              <Chip label={s.overall_grade} size="small" sx={{ bgcolor: gc.bgcolor, color: gc.color, fontWeight: 800 }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={s.status} size="small" sx={{ bgcolor: s.status === 'PASS' ? '#dcfce7' : '#fee2e2', color: s.status === 'PASS' ? '#166534' : '#991b1b', fontWeight: 800 }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}

      {/* ── Change Password Dialog ── */}
      <Dialog open={passwordModal} onClose={() => setPasswordModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0d233a', color: 'white', fontWeight: 800 }}>Change Password</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {passwordError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{passwordError}</Alert>}
          <TextField fullWidth type="password" label="Current Password" value={passwordData.current_password} onChange={e => setPasswordData({ ...passwordData, current_password: e.target.value })} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth type="password" label="New Password" value={passwordData.new_password} onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth type="password" label="Confirm New Password" value={passwordData.confirm_password} onChange={e => setPasswordData({ ...passwordData, confirm_password: e.target.value })} sx={{ mb: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordModal(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} sx={{ bgcolor: '#0d233a', fontWeight: 700 }}>Update Password</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
